import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from './authFetch';
import { Search, Folder, FileText, ChevronRight, Home, Download, ArrowLeft } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function HistoricalFiles() {
  const [letters, setLetters] = useState([]);
  const [clients, setClients] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentLetter, setCurrentLetter] = useState(null);
  const [currentPath, setCurrentPath] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Fetch letter folders on mount
  useEffect(() => {
    fetchLetters();
  }, []);

  const fetchLetters = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(`${BASE_URL}/api/historical/folders/`);
      if (response.ok) {
        const data = await response.json();
        setLetters(data.folders || []);
      } else {
        setError('Failed to load folders');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching letters:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async (letter) => {
    setLoading(true);
    setError(null);
    setCurrentLetter(letter);
    setCurrentPath(null);
    setFiles([]);
    setSearchResults(null);
    setBreadcrumbs([{ type: 'letter', value: letter }]);

    try {
      const response = await authFetch(`${BASE_URL}/api/historical/browse/${letter}/`);
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
      } else {
        setError('Failed to load clients');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async (path) => {
    setLoading(true);
    setError(null);
    setCurrentPath(path);
    setSearchResults(null);

    // Build breadcrumbs from path
    const parts = path.split('/');
    const crumbs = [{ type: 'letter', value: parts[0] }];
    let currentCrumbPath = parts[0];
    for (let i = 1; i < parts.length; i++) {
      currentCrumbPath += '/' + parts[i];
      crumbs.push({ type: 'folder', value: parts[i], path: currentCrumbPath });
    }
    setBreadcrumbs(crumbs);

    try {
      const response = await authFetch(`${BASE_URL}/api/historical/files/${encodeURIComponent(path)}/`);
      if (response.ok) {
        const data = await response.json();
        setFiles(data.items || []);
      } else {
        setError('Failed to load files');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (key, filename) => {
    try {
      const response = await authFetch(`${BASE_URL}/api/historical/download/${encodeURIComponent(key)}/`);
      if (response.ok) {
        const data = await response.json();
        // Open the presigned URL in a new tab
        window.open(data.url, '_blank');
      } else {
        setError('Failed to generate download link');
      }
    } catch (err) {
      setError('Error generating download link');
      console.error('Error downloading file:', err);
    }
  };

  const handleSearch = useCallback(async () => {
    if (searchQuery.trim().length < 2) {
      setSearchResults(null);
      return;
    }

    setSearching(true);
    try {
      const response = await authFetch(`${BASE_URL}/api/historical/search/?q=${encodeURIComponent(searchQuery)}`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      }
    } catch (err) {
      console.error('Error searching:', err);
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        handleSearch();
      } else {
        setSearchResults(null);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const goHome = () => {
    setCurrentLetter(null);
    setCurrentPath(null);
    setClients([]);
    setFiles([]);
    setBreadcrumbs([]);
    setSearchResults(null);
    setSearchQuery('');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Render letter grid (home view)
  const renderLetterGrid = () => (
    <div className="grid grid-cols-6 sm:grid-cols-9 md:grid-cols-13 gap-2">
      {letters.map((letter) => (
        <button
          key={letter}
          onClick={() => fetchClients(letter)}
          className="bg-blue-600 text-white font-bold py-4 px-2 rounded hover:bg-blue-700 transition text-xl"
        >
          {letter}
        </button>
      ))}
    </div>
  );

  // Render client list
  const renderClientList = () => (
    <div className="space-y-2">
      {clients.length === 0 ? (
        <p className="text-gray-500">No clients found in this folder.</p>
      ) : (
        clients.map((client) => (
          <button
            key={client.path}
            onClick={() => fetchFiles(client.path)}
            className="w-full text-left bg-white border border-gray-200 rounded p-3 hover:bg-gray-50 transition flex items-center"
          >
            <Folder className="h-5 w-5 text-yellow-500 mr-3" />
            <span className="font-medium">{client.name}</span>
            <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
          </button>
        ))
      )}
    </div>
  );

  // Render files/folders list
  const renderFileList = () => (
    <div className="space-y-2">
      {files.length === 0 ? (
        <p className="text-gray-500">No files found in this folder.</p>
      ) : (
        files.map((item, index) => (
          <div
            key={index}
            className="w-full bg-white border border-gray-200 rounded p-3 flex items-center"
          >
            {item.type === 'folder' ? (
              <button
                onClick={() => fetchFiles(item.path)}
                className="flex items-center flex-1 text-left hover:text-blue-600"
              >
                <Folder className="h-5 w-5 text-yellow-500 mr-3" />
                <span className="font-medium">{item.name}</span>
                <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
              </button>
            ) : (
              <>
                <FileText className="h-5 w-5 text-gray-500 mr-3" />
                <div className="flex-1">
                  <span className="font-medium">{item.name}</span>
                  <div className="text-sm text-gray-500">
                    {formatFileSize(item.size)} &bull; {formatDate(item.last_modified)}
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(item.key, item.name)}
                  className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition flex items-center"
                >
                  <Download className="h-4 w-4 mr-1" />
                  View
                </button>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );

  // Render search results
  const renderSearchResults = () => (
    <div className="space-y-2">
      <h3 className="text-lg font-semibold mb-3">
        Search Results ({searchResults.length})
      </h3>
      {searchResults.length === 0 ? (
        <p className="text-gray-500">No clients found matching "{searchQuery}"</p>
      ) : (
        searchResults.map((result) => (
          <button
            key={result.path}
            onClick={() => {
              setSearchQuery('');
              setSearchResults(null);
              fetchFiles(result.path);
            }}
            className="w-full text-left bg-white border border-gray-200 rounded p-3 hover:bg-gray-50 transition flex items-center"
          >
            <Folder className="h-5 w-5 text-yellow-500 mr-3" />
            <div className="flex-1">
              <span className="font-medium">{result.name}</span>
              <span className="text-sm text-gray-500 ml-2">({result.letter})</span>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </button>
        ))
      )}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Historical Client Files</h1>
          {(currentLetter || currentPath) && (
            <button
              onClick={goHome}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <Home className="h-5 w-5 mr-1" />
              Back to A-Z
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by client name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              </div>
            )}
          </div>
        </div>

        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && !searchResults && (
          <div className="flex items-center mb-4 text-sm">
            <button
              onClick={goHome}
              className="text-blue-600 hover:text-blue-800 flex items-center"
            >
              <Home className="h-4 w-4 mr-1" />
              Home
            </button>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
                {index === breadcrumbs.length - 1 ? (
                  <span className="text-gray-600 font-medium">{crumb.value}</span>
                ) : (
                  <button
                    onClick={() => {
                      if (crumb.type === 'letter') {
                        fetchClients(crumb.value);
                      } else {
                        fetchFiles(crumb.path);
                      }
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {crumb.value}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Back button for nested folders */}
        {currentPath && currentPath.includes('/') && !searchResults && (
          <button
            onClick={() => {
              const parts = currentPath.split('/');
              if (parts.length > 2) {
                // Go up one folder level
                fetchFiles(parts.slice(0, -1).join('/'));
              } else {
                // Go back to client list
                fetchClients(parts[0]);
              }
            }}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        )}

        {/* Content */}
        {!loading && (
          <>
            {searchResults ? (
              renderSearchResults()
            ) : !currentLetter ? (
              <>
                <p className="text-gray-600 mb-4">
                  Browse historical client files by last name. Select a letter to begin.
                </p>
                {renderLetterGrid()}
              </>
            ) : !currentPath ? (
              renderClientList()
            ) : (
              renderFileList()
            )}
          </>
        )}

        {/* Info footer */}
        {!currentLetter && !loading && (
          <div className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
            These are archived files from the legacy file server. Files are organized by client last name.
          </div>
        )}
      </div>
    </div>
  );
}

export default HistoricalFiles;
